import { installPlaid } from './plaid';
import { installFirebase } from './firebase';
import { initializeTagManager } from './googleTagManager';
import { installSegment } from './segment';
import { installStripe } from './stripe';
import TagManager from 'react-gtm-module';
import {
  gaInstalled,
  installGoogleAnalytics,
  trackGAEvent
} from './googleAnalytics';
import Auth from '../auth/internal/AuthIntegrationInterface';
import FeatheryClient from '../utils/featheryClient';
import { installArgyle } from './argyle';
import { installHeap } from './heap';
import { featheryWindow } from '../utils/browser';
import { installIntercom } from './intercom';
import { installAmplitude } from './amplitude';
import { installMixpanel } from './mixpanel';
import {
  installRudderStack,
  rudderStackInstalled,
  trackRudderEvent
} from './rudderstack';
import { fieldValues, initInfo } from '../utils/init';
import { installPersona } from './persona';
import { authState } from '../auth/LoginForm';
import { installTrustedForm } from './trustedform';
import { installAlloy } from './alloy';

const IMPORTED_URLS = new Set();

export function dynamicImport(
  dependencies: any,
  parallel = true,
  force = false
): any {
  if (typeof dependencies === 'string') dependencies = [dependencies];

  const newDependencies: string[] = [];
  dependencies.forEach((d: any) => {
    let dup = IMPORTED_URLS.has(d);

    if (dup && force) {
      const base = d;
      let counter = 1;
      while (dup) {
        d = `${base}?version=${counter}`;
        dup = IMPORTED_URLS.has(d);
        counter++;
      }
    }

    if (!dup) {
      IMPORTED_URLS.add(d);
      newDependencies.push(d);
    }
  });
  if (newDependencies.length === 0) return Promise.resolve();

  if (parallel) {
    return new Promise((resolve) => {
      global.scriptjsLoadPromise.then(($script: any) => {
        $script.default(newDependencies, (lib: any) => {
          resolve(lib);
        });
      });
    });
  } else {
    return new Promise((resolve) => {
      global.scriptjsLoadPromise.then(($script: any) => {
        $script.default.order(newDependencies, resolve);
      });
    });
  }
}

export async function initializeIntegrations(
  integs: Record<string, any>,
  featheryClient: FeatheryClient
) {
  await Promise.all([
    installArgyle(!!integs.argyle),
    installPlaid(!!integs.plaid),
    installFirebase(integs.firebase),
    installStripe(integs.stripe),
    installSegment(integs.segment),
    installGoogleAnalytics(integs['google-analytics']),
    installHeap(integs.heap),
    installAmplitude(integs.amplitude),
    installMixpanel(integs.mixpanel),
    installIntercom(integs['intercom-embedded']),
    installRudderStack(integs.rudderstack),
    installPersona(integs.persona),
    installAlloy(integs.alloy),
    installTrustedForm(integs.trustedform, featheryClient.formKey)
  ]);

  const gtm = integs['google-tag-manager'];
  if (gtm) initializeTagManager(gtm);
  if (integs.firebase) {
    authState.authFormKey = featheryClient.formKey;
    return Auth.inferLoginOnLoad(featheryClient);
  }
}

export interface ActionData {
  pmField?: any;
  triggerElement?: any;
  triggerElementType?: 'button' | 'container';
  client: any;
  formattedFields?: any;
  updateFieldValues: any;
  integrationData: any;
  targetElement?: any;
}

// Restrict a field-data payload to a configured subset of field keys.
// An empty or absent selection means "send all fields" (the default behavior).
export const filterTrackedFields = <T>(
  fields: Record<string, T>,
  selectedKeys?: string[]
): Record<string, T> => {
  if (!selectedKeys?.length) return fields;
  return selectedKeys.reduce((acc, key) => {
    if (key in fields) acc[key] = fields[key];
    return acc;
  }, {} as Record<string, T>);
};

export function trackEvent(
  integrations: any,
  title: string,
  stepId: string,
  formName: string,
  fieldData?: any
) {
  const metadata: Record<string, string> = {
    formName,
    userId: initInfo().userId ?? ''
  };
  if (stepId) metadata.stepId = stepId;

  // Google Tag Manager
  // @ts-expect-error TS(2551): Property 'initialized' does not exist on type '{ d... Remove this comment to see the full error message
  if (TagManager.initialized) {
    let gtmData = { ...metadata };
    if (fieldData?.['google-tag-manager'])
      gtmData = { ...gtmData, ...fieldData['google-tag-manager'] };
    TagManager.dataLayer({ dataLayer: { ...gtmData, event: title } });
  }
  // RudderStack
  if (rudderStackInstalled) {
    const rudderData: Record<string, any> = { ...metadata };
    if (title === 'FeatheryFormComplete') rudderData.fieldData = fieldValues;
    trackRudderEvent(title, rudderData, integrations?.rudderstack);
  }
  // Google Analytics
  if (gaInstalled) trackGAEvent(formName, title, stepId);
  // Segment
  if (featheryWindow().analytics) {
    const segmentData: any = { ...metadata };
    // Only include userId when identify_user is enabled and userId is non-empty
    if (!integrations?.segment?.metadata?.identify_user || !segmentData.userId)
      delete segmentData.userId;
    if (fieldData?.segment) segmentData.submittedData = fieldData.segment;
    try {
      featheryWindow().analytics.track(title, segmentData);
    } catch (error) {
      console.error(
        `Feathery: Segment integration failed to send event "${title}"`,
        error
      );
    }
  }

  const amplitudeData: any = { ...metadata };
  if (fieldData?.amplitude) amplitudeData.submittedData = fieldData.amplitude;
  if (featheryWindow().amplitude)
    featheryWindow().amplitude.track(title, amplitudeData);

  if (featheryWindow().mixpanel)
    featheryWindow().mixpanel.track(title, metadata);
}
